/**
 * Status chips for a batch and for one of its items, in the table dialect.
 *
 * Four tints, each with one meaning: success for a clean finish, warning
 * for a batch that finished with item errors (the outputs that did work
 * are already delivered, so it is not a failure, but it needs a look),
 * destructive for a failure, and the plain hairline for everything still
 * moving or stopped on request.
 */

import type { BatchItemResponse, BatchResponse } from '@/api/generated/model'
import { Badge, type BadgeProps } from '@/components/ui/badge'

interface ChipSpec {
  label: string
  variant: BadgeProps['variant']
  title: string
}

const BATCH_STATUS: Record<BatchResponse['status'], ChipSpec> = {
  queued: { label: 'Queued', variant: 'outline', title: 'Waiting for a worker' },
  dispatched: { label: 'Dispatched', variant: 'outline', title: 'A worker is starting' },
  running: { label: 'Running', variant: 'default', title: 'Items are being processed' },
  completed: { label: 'Completed', variant: 'success', title: 'Every item finished' },
  completed_with_errors: {
    label: 'Completed with errors',
    variant: 'warning',
    title: 'Some items failed. The rest were delivered',
  },
  failed: { label: 'Failed', variant: 'destructive', title: 'The batch stopped before finishing' },
  cancelled: { label: 'Cancelled', variant: 'secondary', title: 'Stopped on request' },
}

const ITEM_STATUS: Record<BatchItemResponse['status'], ChipSpec> = {
  pending: { label: 'Pending', variant: 'outline', title: 'Not started' },
  running: { label: 'Running', variant: 'default', title: 'In a pipeline stage' },
  done: { label: 'Done', variant: 'success', title: 'Output delivered' },
  skipped: { label: 'Skipped', variant: 'secondary', title: 'The pipeline does not handle this file' },
  failed: { label: 'Failed', variant: 'destructive', title: 'No output for this file' },
}

export function BatchStatusBadge({
  status,
}: {
  status: BatchResponse['status']
}): React.JSX.Element {
  const { label, variant, title } = BATCH_STATUS[status]
  return (
    <Badge variant={variant} title={title} className="whitespace-nowrap">
      {label}
    </Badge>
  )
}

export function BatchItemStatusBadge({
  status,
}: {
  status: BatchItemResponse['status']
}): React.JSX.Element {
  const { label, variant, title } = ITEM_STATUS[status]
  return (
    <Badge variant={variant} title={title} className="whitespace-nowrap">
      {label}
    </Badge>
  )
}
